import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getPerOrderLimitSync, usePerOrderLimit } from "../lib/usePerOrderLimit";
import { fbAddToCart } from "../lib/metaPixel";

const CartContext = createContext(null);

const STORAGE_KEY = "get_reta_cart_v1";
const WISHLIST_STORAGE_KEY = "get_reta_wishlist_v1";

const _readStored = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

// Strip down a product into a wishlist row. Stays compact — wishlist lines
// don't track quantity (you "move to cart" to set one).
const _wishRowFromProduct = (product) => ({
  id: product.id,
  name: product.name,
  dosage: product.dosage,
  price: Number(product.price),
  sale_price:
    product.sale_price !== null &&
    product.sale_price !== undefined &&
    Number(product.sale_price) > 0
      ? Number(product.sale_price)
      : null,
  accent: product.accent,
  image_url: product.image_url,
});

// Same shape but built from a cart line (which already has effective price
// flattened). We preserve original_price so the strike-through can render.
const _wishRowFromCartItem = (it) => ({
  id: it.id,
  name: it.name,
  dosage: it.dosage,
  price: Number(it.original_price ?? it.price),
  sale_price:
    it.original_price && Number(it.price) < Number(it.original_price)
      ? Number(it.price)
      : null,
  accent: it.accent,
  image_url: it.image_url,
});

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => _readStored(STORAGE_KEY));
  const [wishlist, setWishlist] = useState(() =>
    _readStored(WISHLIST_STORAGE_KEY),
  );
  const [open, setOpen] = useState(false);
  // Cart flow step — 'cart' | 'shipping' | 'placed'. Lifted into context
  // so non-drawer surfaces (e.g. SocialProofToast) can react to the
  // customer being mid-checkout. CartDrawer owns the transitions.
  const [step, setStep] = useState("cart");
  // Admin-tunable per-order item cap, hot-fetched on mount. Exposed
  // through the cart context so the drawer + Product Detail stepper
  // share one source of truth and re-render the moment the admin
  // saves a new value.
  const perOrderLimit = usePerOrderLimit();

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlist));
  }, [wishlist]);

  // If the admin lowers the per-order cap mid-session, snap every
  // existing cart line down so the customer can't end up with a row
  // that the backend would now reject at checkout. Re-runs whenever
  // the live cap changes (initial fetch + admin Save).
  useEffect(() => {
    if (!Number.isFinite(perOrderLimit) || perOrderLimit < 1) return;
    setItems((prev) => {
      let changed = false;
      const next = prev.map((i) => {
        if (i.quantity > perOrderLimit) {
          changed = true;
          return { ...i, quantity: perOrderLimit };
        }
        return i;
      });
      return changed ? next : prev;
    });
  }, [perOrderLimit]);

  const addItem = (product, qty = 1) => {
    const cap = getPerOrderLimitSync();
    fbAddToCart(product, qty);
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.id === product.id
            ? { ...i, quantity: Math.min(cap, i.quantity + qty) }
            : i
        );
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          dosage: product.dosage,
          // Honor sale_price when present and strictly lower than the regular
          // price. Cart line totals (and Stripe checkout total) follow suit.
          price:
            product.sale_price !== null &&
            product.sale_price !== undefined &&
            Number(product.sale_price) > 0 &&
            Number(product.sale_price) < Number(product.price)
              ? Number(product.sale_price)
              : Number(product.price),
          original_price: Number(product.price),
          accent: product.accent,
          image_url: product.image_url,
          quantity: qty,
        },
      ];
    });
    // If it was wishlisted, drop the wishlist row — you can't both want it later
    // and have it in your cart at the same time.
    setWishlist((prev) => prev.filter((w) => w.id !== product.id));
    // Note: we intentionally do NOT call `setOpen(true)` here. Earlier
    // we auto-opened the drawer on every add, which masked the
    // header cart-badge bounce animation and forced extra clicks when
    // a customer wanted to add several SKUs in a row. The drawer now
    // only opens when the customer explicitly clicks the cart icon
    // (Amazon/Shopify default flow). The badge bounce + count change
    // are the confirmation signal.
  };

  const updateQty = (id, qty) => {
    setItems((prev) =>
      qty <= 0
        ? prev.filter((i) => i.id !== id)
        : prev.map((i) => (i.id === id ? { ...i, quantity: qty } : i))
    );
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  // Bulk-replace the cart with rows fetched from the server. Used by the
  // /cart?session=... resume flow when a customer clicks the "Resume
  // checkout" link in a nudge email — even on a fresh browser/device.
  const replaceItems = (newItems) => {
    const cap = getPerOrderLimitSync();
    setItems(
      (newItems || []).map((it) => ({
        id: it.id ?? it.product_id,
        name: it.name,
        dosage: it.dosage,
        price: Number(it.price ?? it.unit_price ?? 0),
        original_price: Number(it.original_price ?? it.unit_price ?? it.price ?? 0),
        accent: it.accent,
        image_url: it.image_url,
        quantity: Math.max(1, Math.min(cap, Number(it.quantity) || 1)),
      })),
    );
  };

  const clear = () => setItems([]);

  // ----- Wishlist -----

  const saveCartItemForLater = (id) => {
    const it = items.find((i) => i.id === id);
    if (!it) return;
    setWishlist((prev) =>
      prev.find((w) => w.id === id) ? prev : [...prev, _wishRowFromCartItem(it)]
    );
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const wishlistAdd = (product) => {
    setWishlist((prev) =>
      prev.find((w) => w.id === product.id)
        ? prev
        : [...prev, _wishRowFromProduct(product)]
    );
  };

  const wishlistRemove = (id) => {
    setWishlist((prev) => prev.filter((w) => w.id !== id));
  };

  const wishlistMoveToCart = (id, qty = 1) => {
    const w = wishlist.find((row) => row.id === id);
    if (!w) return;
    addItem(
      {
        id: w.id,
        name: w.name,
        dosage: w.dosage,
        price: w.price,
        sale_price: w.sale_price,
        accent: w.accent,
        image_url: w.image_url,
      },
      qty,
    );
    // addItem already prunes the wishlist entry.
  };

  // Local-fallback subtotal: shown for the brief moment before the live
  // /checkout/quote response arrives. Catalog prices are pre-tax, so the
  // fallback is a plain sum.
  const subtotal = useMemo(
    () => items.reduce((s, i) => s + Number(i.price) * i.quantity, 0),
    [items]
  );
  const count = useMemo(
    () => items.reduce((n, i) => n + i.quantity, 0),
    [items]
  );

  const value = {
    items,
    addItem,
    updateQty,
    removeItem,
    replaceItems,
    clear,
    subtotal,
    count,
    open,
    setOpen,
    step,
    setStep,
    perOrderLimit,
    // Wishlist surface
    wishlist,
    saveCartItemForLater,
    wishlistAdd,
    wishlistRemove,
    wishlistMoveToCart,
    wishlistCount: wishlist.length,
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
